<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { getContext } from 'svelte';
    import { __ } from '@oat-sa-private/ui-core';
    import TextEntryInteraction from '../../interactions/textEntry/TextEntryInteraction.svelte';
    import { formatInputValue } from '../../interactions/util/responseType.js';
    import { getLocale } from '../../util/locale.js';
    import pciJsonCodec from '../../util/pciJsonCodec.js';

    let props = $props();

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(props.itemIdentifier);

    let wrapperElement;
    let showCorrectResponses = $state(false);

    // Keeps the wrapped interaction focusable/clickable in the review renderer.
    const reviewClasses = $derived(`${props.classes || ''} qti-reviewInteraction`.trim());
    const mappedCorrectResponses = $derived(itemContext?.getReviewMappedCorrectResponses?.() || []);
    const mappedResponses = $derived(mappedCorrectResponses
        .map(getMappedResponse)
        .filter(response => response !== null));

    function getMappedResponse(entry) {
        const correctResponse = parseCorrectResponse(entry?.correctResponse);
        if (!correctResponse || !correctResponse[props.responseIdentifier]) {
            return null;
        }

        try {
            const decodedResponse = pciJsonCodec.decode(correctResponse[props.responseIdentifier]);
            return {
                response: formatInputValue(decodedResponse.value, decodedResponse.baseType, props.base || 10, getLocale()),
                score: entry?.score
            };
        } catch {
            return null;
        }
    }

    function parseCorrectResponse(correctResponse) {
        if (!correctResponse) {
            return null;
        }
        if (typeof correctResponse === 'object') {
            return correctResponse;
        }

        try {
            return JSON.parse(correctResponse);
        } catch {
            return null;
        }
    }

    function showMappedResponses() {
        if (mappedResponses.length > 0) {
            showCorrectResponses = true;
        }
    }

    function handleFocusOut() {
        setTimeout(() => {
            showCorrectResponses = mappedResponses.length > 0 && wrapperElement?.contains(document.activeElement);
        });
    }
</script>

<style>
    /* override regular input styles */
    .text-entry-review-interaction {
        display: inline-flex;
        flex-direction: column;
        gap: 0.5rem;
        position: relative;
        vertical-align: top;

        & :global(.qti-textEntryInteraction input[readonly]) {
            pointer-events: all;
            color: var(--color-text-default);
        }

        & :global(.qti-textEntryInteraction input[readonly]:not(.valid)) {
            box-shadow: 0 0 0 var(--border-thin) var(--color-border-warning) inset;
        }

        .correct-responses-popover {
            display: inline-block;
            inset-block-start: 100%;
            inset-inline-start: 0;
            margin-block-start: 0.25rem;
            max-block-size: 20rem;
            overflow-y: auto;
            position: absolute;
            z-index: 1;
            background: var(--color-bg-default);
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.12);
        }

        .correct-responses-table {
            border-collapse: separate;
            border-spacing: 0;
            color: var(--color-text-default);
            font-size: var(--fontsize-body-s);
            width: 100%;
            th,
            td {
                border: 0;
                padding: 0.5rem 1rem;
                text-align: start;
            }

            th + th,
            td + td {
                text-align: end;
            }

            th {
                background-color: var(--color-bg-info, var(--color-bg-table-heading));
                font-weight: bold;
            }
        }
    }
</style>

<div
    class="text-entry-review-interaction"
    bind:this={wrapperElement}
    onfocusin={showMappedResponses}
    onfocusout={handleFocusOut}
>
    <!-- No placeholder, cause in readonly, it can't be distinguished from response -->
    <TextEntryInteraction
        {...props}
        classes={reviewClasses}
        readonly
        placeholderText=""
    />
    {#if showCorrectResponses && mappedResponses.length > 0}
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <div
            class="correct-responses-popover"
            role="region"
            tabindex="0"
            aria-label={__('Correct responses')}
        >
            <table class="correct-responses-table">
                <thead>
                    <tr>
                        <th scope="col">{__('Response')}</th>
                        <th scope="col">{__('Points')}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each mappedResponses as mappedResponse, index (index)}
                        <tr>
                            <td>{mappedResponse.response}</td>
                            <td>{mappedResponse.score}</td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</div>
